import { DatePipe, LowerCasePipe, NgClass } from "@angular/common";
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { HeaderComponent } from "@components/header/header.component";
import { InputComponent } from "@components/input/input.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TabsComponent } from "@components/tabs/tabs.component";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ITab } from "~app/common/components/tabs/tab/itab";
import { HandelssortimentsgruppDTO } from "~app/common/models/common/HandelssortimentsgruppDTO";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { IHandelssortimentsgrupp } from "~app/features/planera/model/IHandelssortimentsgrupp";
import { IProduktionsomraden } from "~app/features/planera/model/IProduktionsomraden";
import { ProduktionsmalPlanMode } from "~app/features/planera/model/ProduktionsmalPlanMode";
import { ArbetsorderMediatorService } from "../services/arbetsorder-mediator.service";
import { ProduktionsmalPlanChartComponent } from "./produktionsmal-plan-chart/produktionsmal-plan-chart.component";
import { ProduktionsmalPlanLegendComponent } from "./produktionsmal-plan-legend/produktionsmal-plan-legend.component";
import { IProduktionsmalPlanResult, ProduktionsmalPlanService } from "./produktionsmal-plan.service";

@Component({
    selector: "rask-produktionsmal-plan",
    templateUrl: "./produktionsmal-plan.component.html",
    styleUrls: ["./produktionsmal-plan.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        SelectorComponent,
        FormsModule,
        DatepickerComponent,
        NgClass,
        ButtonComponent,
        InputComponent,
        ProduktionsmalPlanLegendComponent,
        TabsComponent,
        ProduktionsmalPlanChartComponent,
        LowerCasePipe,
        DatePipe
    ]
})
export class ProduktionsmalPlanComponent implements OnInit, OnChanges, OnDestroy {
    readonly webinstallningarKey = "Planera.produktionsmalPlanFilter";

    @Input() handelssortimentsgrupper: HandelssortimentsgruppDTO[];
    hsgTabs: ITab[];
    minDate = new Date(new Date().setDate(1));
    @Input() mode: string;
    period: Date;
    produktionsmalPlan: IProduktionsmalPlanResult;
    @Input() produktionsomrade: IProduktionsomraden;
    @Input() produktionsomradeFilterId: number;
    @Input() produktionsomraden: IProduktionsomraden[];
    selectedHandelssortimentsgrupp: IHandelssortimentsgrupp;
    selectedHsgKod: string;
    selectedHsgTab: ITab;

    private readonly busyService = inject(BusyService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly planeraArbetsorderMediatorService = inject(ArbetsorderMediatorService);
    private readonly produktionsmalPlanService = inject(ProduktionsmalPlanService);
    private readonly webinstallningService = inject(WebinstallningarService);

    get resultProduktionsomrade() {
        return this.produktionsmalPlanService.getProduktionsomradeFromResult(this.produktionsomraden, this.produktionsmalPlan);
    }

    canShow() {
        const produktionsomradeId = this.isDetailMode() ? this.produktionsomradeFilterId : this.produktionsomrade?.id;
        return produktionsomradeId && this.isCorrectPeriod();
    }

    getNonEmptyHandelssortimentsgrupper = () => this.produktionsmalPlan?.handelssortimentsgrupper.filter(a => a.handelssortiment.length > 0);

    isCorrectPeriod() {
        const periodDate = new Date(this.period);
        return periodDate.setHours(2, 0, 0) >= this.minDate.setHours(1, 0, 0);
    }

    isDetailMode = () => this.mode === ProduktionsmalPlanMode.detail;

    loadWebinstallningar() {
        const installningar = this.webinstallningService.get<any>(this.webinstallningarKey);
        if (installningar) {
            this.period = installningar.period;
            this.produktionsomrade = this.produktionsomraden?.find(p => p.id === installningar.produktionsomradeId);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.isDetailMode()) {
            this.loadWebinstallningar();
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        if (this.isDetailMode()) {
            this.planeraArbetsorderMediatorService.arbetsorder
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(changeEvent => {
                    if (changeEvent) {
                        this.produktionsomradeFilterId = changeEvent?.arbetsorderWrapper?.arbetsorder?.produktionsomradeId;
                    }
                });
        }
    }

    poFormatter = (v: IProduktionsomraden) => v.namn;

    poGroupBy = (v: IProduktionsomraden) => v.grupp;

    saveWebinstallningar() {
        this.webinstallningService.setAsync(this.webinstallningarKey, { period: this.period,
            produktionsomradeId: this.produktionsomrade.id });
    }

    showGraph() {
        const produktionsomradeId = this.isDetailMode() ? this.produktionsomradeFilterId : this.produktionsomrade.id;
        const request = this.produktionsmalPlanService.getProduktionsmalPlan(produktionsomradeId, this.period);
        this.busyService.addWork(request).subscribe(r => {
            this.produktionsmalPlan = r;
            if (!this.isDetailMode()) {
                this.saveWebinstallningar();
            } else {
                this.updateHsgTabs();
                this.setSelectedHandelssortimentsgrupp();
            }
        });
    }

    private setSelectedHandelssortimentsgrupp() {
        const filtered = this.getNonEmptyHandelssortimentsgrupper();
        if (filtered && filtered.length > 0) {
            this.selectedHsgTab = this.hsgTabs.find(t => t.tabId === filtered[0].kod);
        } else {
            this.selectedHsgTab = null;
        }
    }

    private updateHsgTabs() {
        this.hsgTabs = [];
        this.getNonEmptyHandelssortimentsgrupper().forEach(hsg => {
            const tab = {
                tabId: hsg.kod,
                label: hsg.kortnamn,
                onTabSelected: new EventEmitter(),
                ignoreIconClick: false
            };
            this.hsgTabs.push(tab);
            tab.onTabSelected.pipe(
                takeUntil(this.ngUnsubscribe$)
            ).subscribe(() => {
                this.selectedHsgKod = hsg.kod;
            });
        });
    }
}
